// 庭グリッドの純ロジック。配置・撤去・回転はすべて副作用のない純関数で、
// 検証（範囲外・重複・チップ不足）を内部で行い、不正な操作は元の状態を返す。
//
// デザイン方針: 撤去は全額返金（やり直し無料）。盆栽的な試行錯誤を阻害しない。

import type { GameState, Island } from './state';
import {
  ATTRIBUTE_KEYS,
  getDeco,
  leveledAttributes,
  clampLevel,
  upgradeCost,
  totalInvested,
  isUpgradableDeco,
  ENTRANCE_DECO_ID,
  type AttributeKey,
  type AttributeVector,
} from './decos';
import {
  isPlantDeco,
  isAncient,
  plantAttributes,
  createPlanting,
  pruneForm,
  type PruneStyle,
} from './plants';
import type { GardenAttributeTotals } from './customers';
import { specialAttribute } from './menu';
import { vitalityAt, isWiltingDeco } from './wilt';
import { activeSynergies, synergyAttributeTotals } from './synergy';

/**
 * 庭グリッドの一辺のセル数。v0.5.1 で 8 → 12 に拡張（ユーザーレビュー反映）。
 * CELL_SIZE は据え置き（0.8）なので span は 12×0.8 = 9.6。島の円形上面に
 * 合わせて角のセルは「無効セル」になり、円なりに端が欠ける自然な形になる。
 */
export const GRID_SIZE = 12;

/**
 * グリッド 1 セルのワールド単位幅。座標変換（diorama）と有効セル判定（ここ）の
 * 双方で使う正本。render 層（diorama）はこの値を import して共有する
 * （sim が描画に依存しないよう、定数の所有は sim 側に置く）。
 */
export const CELL_SIZE = 0.8;

/**
 * 有効セル判定の半径（グリッド中心＝原点からセル中心までの距離の上限、ワールド単位）。
 *
 * 島の上面（diorama の asteroid 上面シリンダー、半径 ~5.2）に収まるよう、見た目で
 * 少し内側の 4.8 を採用する。セル中心がこの半径より外にある角のセルは無効
 * （配置不可・枠も描かない）にして、円形の島なりに端を欠けさせる。
 */
export const VALID_RADIUS = 4.8;

/**
 * グリッド中心（原点）からセル中心までの距離（ワールド単位）。
 * gridToWorld と同じ式で「島ローカル XZ のセル中心」を出し、原点からの距離を返す。
 * 座標変換の正本（diorama）に依存しないよう、ここでは式を内製する
 * （CELL_SIZE と GRID_SIZE だけで決まる純計算なので重複を許容する）。
 */
function cellCenterRadius(gx: number, gy: number): number {
  const span = GRID_SIZE * CELL_SIZE;
  const half = span / 2;
  const x = (gx + 0.5) * CELL_SIZE - half;
  const z = (gy + 0.5) * CELL_SIZE - half;
  return Math.hypot(x, z);
}

/**
 * (x, y) が有効セルか（**ホーム島の円形上面**に収まるか）。範囲外は false。
 * 無効セルには配置できず、グリッド枠も描かない（円なりに端が欠ける）。
 *
 * これは shape='round'（ホーム島の形）の述語そのもの。拡張島の形状判定は
 * isCellInShape(x, y, shape) を使う。ホーム島は round 固定なので isValidCell で読む
 * （カフェ・入口・石畳パス・席・導線がこの円形に依存しているため変えない）。
 */
export function isValidCell(x: number, y: number): boolean {
  if (!inBounds(x, y)) return false;
  return cellCenterRadius(x, y) <= VALID_RADIUS;
}

// =========================================================================
// 島ごとの形状バリアント（v0.7.3）
// =========================================================================
//
// 拡張島（index ≥1）の上面シルエットを島ごとに変える。**見た目（小惑星上面メッシュの
// 輪郭）と有効セル判定（配置可能マス）を必ずセットで**形状に一致させる（マスが浮く/
// 欠ける不整合を作らない）。ホーム島（index 0）は round（現行の円）に固定する。
//
// 各形状は「グリッド中心＝原点」からのセル中心ワールド XZ（cellCenterXZ）を受けて
// 「そのセルが島上面に乗るか」を返す純述語。すべて span(9.6)/half(4.8) の枠内に収まる
// よう設計する（グリッド外には出ない）。乱数なし・決定的。

/**
 * 島の上面形状の種類（v0.7.3、v0.7.4 で square / twin を追加）。
 *
 * v0.7.4 のラインアップ変更:
 *  - `square`: 正方形の島（島2 = index1。oval を置き換え）。中央寄りの正方領域が有効セル。
 *  - `twin`: 丸い島が 2 つに分かれて浮き、橋でつながった島（島3 = index2。hex を置き換え）。
 *    有効セルは「2 つの円の和＋その間をつなぐ橋（細い通路セル列）」。
 * `oval`/`hex` は使わなくなったが、後方互換（過去の reconcile・描画分岐）のため列挙には残す。
 */
export type IslandShape = 'round' | 'oval' | 'hex' | 'clover' | 'teardrop' | 'square' | 'twin';

/** 形状の全列挙（テスト網羅・描画の分岐に使う単一正本）。 */
export const ISLAND_SHAPES: readonly IslandShape[] = [
  'round', 'oval', 'hex', 'clover', 'teardrop', 'square', 'twin',
] as const;

/**
 * 島 index から決定的に形状を割り当てる（v0.7.3、v0.7.4 でラインアップ更新）。乱数なし・index 導出。
 *  - 0 = ホーム島 → round（現行の円。カフェ・入口・導線が依存するので固定・不変）。
 *  - 1 → square（正方形）/ 2 → twin（丸2つ＋橋）/ 3 → clover（4つ葉・凹凸）/ 4 → teardrop（卵形）。
 * 上限（MAX_ISLANDS=5）を超える index は round にフォールバック（安全側）。
 */
export function shapeForIslandIndex(islandIndex: number): IslandShape {
  switch (islandIndex) {
    case 0: return 'round';
    case 1: return 'square';
    case 2: return 'twin';
    case 3: return 'clover';
    case 4: return 'teardrop';
    default: return 'round';
  }
}

/** グリッド中心（原点）からのセル中心ワールド XZ（cellCenterRadius と同じ式）。 */
function cellCenterXZ(gx: number, gy: number): { x: number; z: number } {
  const span = GRID_SIZE * CELL_SIZE;
  const half = span / 2;
  return { x: (gx + 0.5) * CELL_SIZE - half, z: (gy + 0.5) * CELL_SIZE - half };
}

/**
 * セル中心 (x,z)（原点中心ワールド XZ）が形状 shape の上面に乗るかの純述語。
 * 形状ごとに半径・楕円・六角・4つ葉・卵形を判定する。round は VALID_RADIUS の円。
 *
 * 設計方針: いずれも half=4.8 の枠内に収め、round と同程度のセル数（中央〜中盤が有効、
 * 端は形に沿って欠ける）にする。描画側（diorama）はこの述語と同じ閾値で輪郭メッシュを
 * 作るため、見た目とマスが一致する。
 */
function cellInShapeXZ(x: number, z: number, shape: IslandShape): boolean {
  switch (shape) {
    case 'round':
      return Math.hypot(x, z) <= VALID_RADIUS;
    case 'oval': {
      // 横長楕円: X 方向に広く（rx=5.4）、Z 方向に狭く（rz=3.7）。grid の枠（±4.8）に収める。
      const rx = 5.4, rz = 3.7;
      return (x * x) / (rx * rx) + (z * z) / (rz * rz) <= 1;
    }
    case 'hex': {
      // 平頂六角形（flat-top）。外接半径 R=5.0。|x| <= R*√3/2 かつ六角の斜辺内側。
      // （v0.7.4 でラインアップからは外したが、後方互換のため判定は残す。）
      const R = 5.0;
      const ax = Math.abs(x), az = Math.abs(z);
      if (az > (Math.sqrt(3) / 2) * R) return false; // 上下の平らな辺
      // 斜辺: x/R + z/(√3 R) の組み合わせ（六角の標準不等式）。
      return ax <= R && (Math.sqrt(3) / 2) * R >= az &&
        az <= (Math.sqrt(3)) * (R - ax) + 1e-9;
    }
    case 'square': {
      // 正方形（v0.7.4、島2）: 中央寄りの正方領域。half=4.8 の枠内に収め、round と同程度の
      // セル数になるよう一辺を ~8.0（half ±4.0）にする。上面メッシュ・セル枠も同じ正方輪郭。
      const HS = 4.0; // 半辺（中心からの距離の上限、ワールド単位）
      return Math.abs(x) <= HS && Math.abs(z) <= HS;
    }
    case 'clover': {
      // 4つ葉（v0.7.4 で輪郭とマスの一致を厳密化）: 中央コア + 対角 4 方向の葉。
      //
      // 旧実装は葉の中心が遠く（off=2.3, 半径 2.5）原点から見て星状（star-convex）でなく、
      // 輪郭を「原点からの半径最大」で引く shapeOutline が葉の外縁を取りこぼし、葉のセルが
      // 描画輪郭からはみ出していた。葉を中央コアへ寄せ（off を縮め）半径もコアと十分重ねて、
      // どの角度の動径もコア→葉と単調に内側を通る（star-convex に近い）形にする。これで
      // shapeOutline（動径サンプリング）の輪郭と有効セルが厳密に一致し、はみ出しが消える。
      // パラメータは「64 分割の shapeOutline 輪郭の内側に全有効セルが収まる」よう数値検証で
      // 決めた（core=3.4 / off=2.1 / lr=2.4 ではみ出し 0・有効セル 108）。葉を縮め過ぎず、かつ
      // 動径輪郭が取りこぼす葉の先端に有効セル中心が乗らないようにしている。
      const core = 3.4;
      if (Math.hypot(x, z) <= core) return true;
      const off = 2.1, lr = 2.4; // 中心 (±2.1,±2.1)・半径 2.4（コア半径 3.4 と大きく重なる）
      const lobes: [number, number][] = [
        [off, off], [off, -off], [-off, off], [-off, -off],
      ];
      for (const [cx, cz] of lobes) {
        if (Math.hypot(x - cx, z - cz) <= lr) return true;
      }
      return false;
    }
    case 'twin': {
      // 双子島（v0.7.4、島3）: 丸い島が 2 つに分かれて浮き、橋でつながる。
      //
      // 有効セル = 「左右 2 つの円の和」＋「その間をつなぐ橋（細い通路の矩形帯）」。
      // 2 円は中心 (±cx, 0)・半径 r。橋は X が両円中心の間、|z| が細い帯（橋幅）に入る列。
      // 見た目（diorama / carousel）も 2 ディスク＋橋メッシュで作り、マスと一致させる。
      // 幾何は TWIN_GEOM（描画と共有の単一正本）を使う。v0.7.5 で 2 円を離し（橋を渡す）。
      const { centerX: cx, radius: r, bridgeHalfW } = TWIN_GEOM;
      if (Math.hypot(x - cx, z) <= r) return true; // 右の円
      if (Math.hypot(x + cx, z) <= r) return true; // 左の円
      // 橋: 2 円の中心の間（|x| <= cx）で、細い帯（|z| <= 半幅）。橋でつなぐ通路。
      if (Math.abs(x) <= cx && Math.abs(z) <= bridgeHalfW) return true;
      return false;
    }
    case 'teardrop': {
      // 卵形（egg）: 基本は楕円だが Z+ 側（手前）をすぼめ Z- 側（奥）を丸く膨らませる。
      // 横半径 rx=4.4。z による縦半径を可変にして卵の非対称を作る。
      const rx = 4.4;
      // 奥（z<0）は rz=4.6 で丸く、手前（z>0）は rz=3.4 ですぼむ。
      const rz = z < 0 ? 4.6 : 3.4;
      return (x * x) / (rx * rx) + (z * z) / (rz * rz) <= 1;
    }
    default:
      return Math.hypot(x, z) <= VALID_RADIUS;
  }
}

/**
 * (x, y) が形状 shape の有効セルか（島の上面に乗るか）。範囲外は false（v0.7.3）。
 * 無効セルには配置できず、グリッド枠も描かない（形なりに端が欠ける）。
 * shape='round' は isValidCell と完全一致（ホーム島の述語）。
 */
export function isCellInShape(x: number, y: number, shape: IslandShape): boolean {
  if (!inBounds(x, y)) return false;
  if (shape === 'round') return isValidCell(x, y);
  const { x: wx, z: wz } = cellCenterXZ(x, y);
  return cellInShapeXZ(wx, wz, shape);
}

/**
 * 連続点 (wx, wz)（原点中心ワールド XZ）が形状 shape の内側かを返す（v0.7.3、描画用）。
 * セル判定（isCellInShape）と同じ閾値を使うので、輪郭メッシュとマスが一致する。描画層
 * （diorama）が上面の輪郭ジオメトリを作るのに使う。
 */
export function pointInShapeXZ(wx: number, wz: number, shape: IslandShape): boolean {
  return cellInShapeXZ(wx, wz, shape);
}

/**
 * twin（双子島）の幾何定数（v0.7.4）。sim の有効セル述語（cellInShapeXZ）と描画層
 * （diorama / carousel の 2 ディスク＋橋メッシュ）で共有する単一正本。座標はワールド単位。
 */
export const TWIN_GEOM = {
  /**
   * 各円の中心 X（左 -, 右 +）。v0.7.5 で 2.7→3.0 に広げ、半径も縮めて 2 円を明確に分離
   * （内側の縁が x=±1.0 になり、中央に幅 2.0 の隙間ができる）。隙間は橋モデルで渡す。
   */
  centerX: 3.0,
  /** 各円の半径。v0.7.5 で 2.5→2.0（分離を出すため）。 */
  radius: 2.0,
  /** 橋の半幅（|z| <= これ で橋帯）。 */
  bridgeHalfW: 0.6,
} as const;

/**
 * twin（双子島）の **描画専用** の高さパラメータ（v0.7.6）。sim の有効セル述語・経済・
 * 決定性には一切関与しない（XZ のセル座標・配置可否は不変）。左ディスクを上げ・右ディスクを
 * 下げて 2 島に高低差をつけ、間をアーチ橋で結ぶための単一正本。diorama（島本体・橋メッシュ）
 * と garden_view（その島の上に乗るデコ・枠・ピップ等のセル表示 Y）が共有する。
 */
export const TWIN_HEIGHT = {
  /** 左ディスクの上げ量／右ディスクの下げ量（左 +DELTA, 右 -DELTA）。 */
  delta: 0.45,
  /**
   * 円の内縁の |x|（centerX - radius = 3.0 - 2.0 = 1.0）。この内側（橋帯）で高さを線形補間する。
   * 左ディスク領域(x ≦ -innerEdge)→ +delta、右ディスク領域(x ≧ +innerEdge)→ -delta。
   */
  innerEdge: TWIN_GEOM.centerX - TWIN_GEOM.radius,
} as const;

/**
 * twin の **描画専用** セル表面 Y オフセット（v0.7.6、純関数・副作用なし）。ワールド X
 * （原点中心、島ローカル XZ の x）を受け、その位置のディスク上面の高さオフセットを返す:
 *  - 左ディスク領域 (x ≦ -innerEdge) → +delta（高い）
 *  - 右ディスク領域 (x ≧ +innerEdge) → -delta（低い）
 *  - 中間（橋帯, |x| < innerEdge）→ アーチ橋の傾きと連続するよう x で線形補間。
 *
 * これは「見た目の高さ」だけを返す（ピッキングやセル座標には影響しない）。非 twin の島は
 * 呼び出し側が 0 を使う（従来どおり平ら）。
 */
export function twinSurfaceY(worldX: number): number {
  const { delta, innerEdge } = TWIN_HEIGHT;
  if (worldX <= -innerEdge) return delta;
  if (worldX >= innerEdge) return -delta;
  // 橋帯: x=-innerEdge で +delta、x=+innerEdge で -delta の線形補間（アーチ床の両端高さと連続）。
  return -(worldX / innerEdge) * delta;
}

/**
 * 形状 shape の輪郭ポリゴン（原点中心ワールド XZ の頂点列、CCW、閉じない）を返す純関数
 * （v0.7.3、描画用）。各角度方向に半径を求めてサンプリングする。`segments` は角度分割数。
 * round は円なので等半径。描画層が上面メッシュ（THREE.Shape 等）に使う。
 *
 * 注意（v0.7.4）: `twin` は star-convex でない（2 円が原点から離れて分離する）ため、動径
 * サンプリングでは 2 円の分離・橋が表現できない。`twin` の描画は専用に 2 ディスク＋橋メッシュ
 * を組む（diorama/carousel）ので、ここでは呼ばない前提（呼ばれても破綻しない単一輪郭を返す）。
 */
export function shapeOutline(shape: IslandShape, segments = 64): { x: number; z: number }[] {
  const pts: { x: number; z: number }[] = [];
  const maxR = (GRID_SIZE * CELL_SIZE) / 2; // half = 4.8（探索の上限）
  // clover など非凸（中心から見て穴あき）形状もあるので、細かく外向きに走査して
  // 「内側である最遠の半径」を採る（単純な二分探索だと隙間に落ちることがある）。
  const STEPS = 96;
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const dx = Math.cos(a);
    const dz = Math.sin(a);
    let far = 0;
    for (let s = 1; s <= STEPS; s++) {
      const r = (s / STEPS) * maxR;
      if (cellInShapeXZ(dx * r, dz * r, shape)) far = r;
    }
    pts.push({ x: dx * far, z: dz * far });
  }
  return pts;
}

/**
 * カフェ本体が被っていて配置できないセル（配置禁止セル）。
 *
 * カフェは island ローカルで (-1.6, -1.6) を中心に幅 2.2 ほどを占める。グリッドは
 * 原点中心なので、(-1.6,-1.6) は新しい 12×12 ではグリッド中心 (6,6) からセル幅
 * (-2,-2) ぶんずれた 2×2 ブロック＝ (gx,gy ∈ {4,5}) にあたる。その足元を塞ぐ。
 * ここに置こうとした設置は拒否し、UI も配置不可（赤）で示す。
 *
 * 算出: gx = round((cafeX + half)/CELL - 0.5)。cafeX=-1.6, half=4.8, CELL=0.8
 *   → (3.2/0.8) - 0.5 = 4 - 0.5 = 3.5 → カフェ中心は (4,4) と (5,5) の境目なので
 *   その 2×2（{4,5}×{4,5}）を占有扱いにする。
 *
 * セットの要素は cellIndex(gx, gy) の値（行優先 1 次元インデックス）。
 */
export const BLOCKED_CELLS: ReadonlySet<number> = new Set(
  [
    [4, 4],
    [4, 5],
    [5, 4],
    [5, 5],
  ].map(([gx, gy]) => gy * GRID_SIZE + gx),
);

/**
 * (x, y) が配置禁止セルか（範囲外・無効セル・カフェ被り・入口ゲートは true 扱い＝置けない）。
 * 入口ゲートは固定（自動配置・撤去不可）なので、その上にプレイヤーは置けない（v0.7.0）。
 *
 * これは**ホーム島（index 0）の規約**。カフェ被り・入口はホーム島固有なので、拡張島では
 * これではなく isPlaceableCell（円外だけ禁止）で判定する（v0.7.1）。
 */
export function isBlocked(x: number, y: number): boolean {
  if (!isValidCell(x, y)) return true; // 範囲外・島の外（無効セル）は置けない
  if (isEntranceCell(x, y)) return true; // 入口ゲートの固定セル
  return BLOCKED_CELLS.has(cellIndex(x, y));
}

/**
 * (x, y) が島 islandIndex に配置可能か（v0.7.1、v0.7.3 で形状対応）。
 *
 *  - ホーム島（0）: isBlocked の逆（カフェ被り・入口・円外を禁止）。round 形状固定。
 *  - 拡張島（≥1）: カフェも入口も無いので**その島の形状の有効セルなら配置可**
 *    （カフェ被りセルも置ける）。形状は shapeForIslandIndex(islandIndex) で決定的に決まる。
 */
export function isPlaceableCell(x: number, y: number, islandIndex = 0): boolean {
  if (islandIndex === 0) return !isBlocked(x, y);
  return isCellInShape(x, y, shapeForIslandIndex(islandIndex)); // 拡張島は形状なりに自由
}

/** 向き（0/1/2/3 = 0°/90°/180°/270°）。回転は M4 の成長表現にも使う土台。 */
export type Orientation = 0 | 1 | 2 | 3;

/** 1 セルの中身。空セルは null。 */
export interface GardenCell {
  /** デコ ID（DECO_CATALOG の id）。 */
  decoId: string;
  /** 向き。 */
  orientation: Orientation;
  /**
   * タイルレベル（1..MAX_LEVEL、v0.7.0）。アップグレードで属性が強くなる。
   * 省略時（旧セーブ）は 1 とみなす（gardenAttributes 等は cellLevel で読む）。
   */
  level?: number;
}

/**
 * 入口ゲートの固定セル（v0.7.0）。島手前（カメラ側＝高 gx・高 gy）のエッジに置く。
 * 新規ゲーム・セーブ移行で必ずここに 1 つだけ自動配置し、撤去できないようにする。
 * カフェは奥手 (4..5) なので、入口(手前) → カフェ(奥) の対角線が自然な導線になる。
 */
export const ENTRANCE_CELL = { gx: 9, gy: 9 } as const;

/** 入口ゲートのセル index（行優先 1 次元）。 */
export const ENTRANCE_INDEX = ENTRANCE_CELL.gy * GRID_SIZE + ENTRANCE_CELL.gx;

/** セルのレベルを読む（未設定は 1）。 */
export function cellLevel(cell: GardenCell | null): number {
  if (!cell) return 1;
  return clampLevel(cell.level ?? 1);
}

/** (x,y) のセルが入口ゲートか。 */
export function isEntranceCell(x: number, y: number): boolean {
  return x === ENTRANCE_CELL.gx && y === ENTRANCE_CELL.gy;
}

/** 庭グリッド: GRID_SIZE×GRID_SIZE の行優先 1 次元配列（null = 空）。 */
export type GardenGrid = (GardenCell | null)[];

/** 空の庭グリッドを生成する。 */
export function createEmptyGarden(): GardenGrid {
  return new Array<GardenCell | null>(GRID_SIZE * GRID_SIZE).fill(null);
}

/** 座標がグリッド範囲内か。 */
export function inBounds(x: number, y: number): boolean {
  return Number.isInteger(x) && Number.isInteger(y) && x >= 0 && x < GRID_SIZE && y >= 0 && y < GRID_SIZE;
}

/** (x, y) → 1 次元インデックス。範囲チェックは呼び出し側の責務。 */
export function cellIndex(x: number, y: number): number {
  return y * GRID_SIZE + x;
}

/** (x, y) のセルを取得（範囲外は null）。 */
export function cellAt(garden: GardenGrid, x: number, y: number): GardenCell | null {
  if (!inBounds(x, y)) return null;
  return garden[cellIndex(x, y)] ?? null;
}

/**
 * 対象島（islandIndex）を取得する（v0.7.1）。範囲外（未アンロック等）は null。
 * 庭の純関数はこれで島を読み、writeIsland で 1 島だけ差し替えた新 state を返す。
 */
export function islandAt(state: GameState, islandIndex: number): Island | null {
  return state.islands[islandIndex] ?? null;
}

/**
 * 1 島だけ差し替えた新しい GameState を返す純関数（v0.7.1）。islands 配列を slice で
 * 浅いコピーして該当 index を置き換える（他島・他フィールドは参照を共有）。
 */
function writeIsland(state: GameState, islandIndex: number, island: Island): GameState {
  const islands = state.islands.slice();
  islands[islandIndex] = island;
  return { ...state, islands };
}

/**
 * デコを (x, y) に配置する。検証に通らなければ状態をそのまま返す（拒否）。
 *
 * 拒否条件: 範囲外 / 既に何か置かれている / 未知のデコ ID / チップ不足。
 * 成功時はチップをコストぶん減らし、新しい GameState を返す（入力は破壊しない）。
 */
export function placeDeco(
  state: GameState,
  x: number,
  y: number,
  decoId: string,
  orientation: Orientation = 0,
  islandIndex = 0,
): GameState {
  if (!inBounds(x, y)) return state;
  // カフェ被り・入口の配置禁止はホーム島（0）のみ。拡張島にはカフェも入口も無いので、
  // 占有ブロック {4,5}×{4,5} も入口セルも普通に置ける（無効セル＝円外だけは全島禁止）。
  if (!isPlaceableCell(x, y, islandIndex)) return state;
  const deco = getDeco(decoId);
  if (!deco) return state;
  const island = state.islands[islandIndex];
  if (!island) return state; // 未アンロックの島

  const idx = cellIndex(x, y);
  if (island.garden[idx] != null) return state; // 重複拒否
  if (state.chips < deco.cost) return state; // チップ不足拒否

  const garden = island.garden.slice();
  garden[idx] = { decoId, orientation, level: 1 };

  // 植物デコなら、その場に生育状態（成長 0・seed 由来の樹形）を作る。
  let plantings = island.plantings;
  if (isPlantDeco(decoId)) {
    plantings = { ...plantings, [String(idx)]: createPlanting(state.seed, decoId, idx) };
  }

  return {
    ...writeIsland(state, islandIndex, { garden, plantings }),
    chips: state.chips - deco.cost,
  };
}

/**
 * 入口ゲートが固定セルに存在することを保証する（v0.7.0）。
 *
 * 新規ゲーム・セーブ移行で呼ぶ。入口セルに別のデコが居れば退かす（庭が成立する
 * 不変条件: 入口は常に 1 つ）。既に入口があれば何もしない。チップは触らない
 * （自動配置はコスト 0）。入力は破壊しない純関数。
 */
export function ensureEntrance(garden: GardenGrid): GardenGrid {
  const existing = garden[ENTRANCE_INDEX];
  if (existing && existing.decoId === ENTRANCE_DECO_ID) return garden;
  const next = garden.slice();
  next[ENTRANCE_INDEX] = { decoId: ENTRANCE_DECO_ID, orientation: 0, level: 1 };
  return next;
}

/**
 * (x, y) のデコをアップグレードする純関数（v0.7.0）。検証に通らなければ状態を
 * そのまま返す（拒否）。
 *
 * 拒否条件: 範囲外 / 空セル / アップグレード不可デコ（入口等）/ 既に最大レベル /
 * チップ不足。成功時はチップを upgradeCost ぶん減らし、level を +1 した新しい
 * GameState を返す（属性は gardenAttributes 側が level 倍率で強くする）。
 */
export function upgradeDeco(state: GameState, x: number, y: number, islandIndex = 0): GameState {
  if (!inBounds(x, y)) return state;
  const island = state.islands[islandIndex];
  if (!island) return state;
  const idx = cellIndex(x, y);
  const cell = island.garden[idx];
  if (cell == null) return state;
  if (!isUpgradableDeco(cell.decoId)) return state;
  const level = cellLevel(cell);
  const cost = upgradeCost(cell.decoId, level);
  if (!Number.isFinite(cost)) return state; // 最大レベル
  if (state.chips < cost) return state;

  const garden = island.garden.slice();
  garden[idx] = { ...cell, level: level + 1 };
  return {
    ...writeIsland(state, islandIndex, { garden, plantings: island.plantings }),
    chips: state.chips - cost,
  };
}

/**
 * (x, y) のデコを撤去する。空セルなら状態をそのまま返す。
 * 撤去はコスト全額をチップに返金する（やり直し無料の方針）。
 */
export function removeDeco(state: GameState, x: number, y: number, islandIndex = 0): GameState {
  if (!inBounds(x, y)) return state;
  // 入口ゲートはホーム島の固定セル（撤去不可）。拡張島には入口が無いので制約しない（v0.7.1）。
  if (islandIndex === 0 && isEntranceCell(x, y)) return state;
  const island = state.islands[islandIndex];
  if (!island) return state;
  const idx = cellIndex(x, y);
  const cell = island.garden[idx];
  if (cell == null) return state;

  const deco = getDeco(cell.decoId);
  // 未知 ID（壊れたセーブ等）でも撤去は許す。返金は分かる範囲で。
  // v0.7.0: 返金は「投入総額」（base + 各 level のアップグレードコスト）を全額返す。
  const refund = deco ? totalInvested(cell.decoId, cellLevel(cell)) : 0;

  const garden = island.garden.slice();
  garden[idx] = null;

  // 植物だった場合は生育状態も消す（撤去でリセット。再植は芽から）。
  let plantings = island.plantings;
  if (plantings[String(idx)] != null) {
    plantings = { ...plantings };
    delete plantings[String(idx)];
  }

  return {
    ...writeIsland(state, islandIndex, { garden, plantings }),
    chips: state.chips + refund,
  };
}

/**
 * (x, y) に植わった植物を剪定する純関数。植物でないセル・空セルはそのまま返す。
 *
 * 剪定は樹形を動かし、樹形によって属性が変わる（plants.pruneForm）:
 *  - のばす（extend）→ 緑寄り / ととのえる（tidy）→ 静寂寄り。
 * 剪定は無料・いつでも・失敗なし（盆栽的試行錯誤を阻害しない方針）。成長段階は
 * 変えない（剪定で巻き戻ったり枯れたりしない）。
 */
export function pruneDeco(
  state: GameState,
  x: number,
  y: number,
  style: PruneStyle,
  islandIndex = 0,
): GameState {
  if (!inBounds(x, y)) return state;
  const island = state.islands[islandIndex];
  if (!island) return state;
  const idx = cellIndex(x, y);
  const cell = island.garden[idx];
  if (cell == null || !isPlantDeco(cell.decoId)) return state;

  const planting = island.plantings[String(idx)];
  if (planting == null) return state;

  const plantings = {
    ...island.plantings,
    [String(idx)]: { ...planting, form: pruneForm(planting.form, style) },
  };
  return writeIsland(state, islandIndex, { garden: island.garden, plantings });
}

/**
 * (x, y) のデコを 90° 回転する（時計回り）。空セルなら状態をそのまま返す。
 * 回転は無料（盆栽的試行錯誤の一部）。
 */
export function rotateDeco(state: GameState, x: number, y: number, islandIndex = 0): GameState {
  if (!inBounds(x, y)) return state;
  const island = state.islands[islandIndex];
  if (!island) return state;
  const idx = cellIndex(x, y);
  const cell = island.garden[idx];
  if (cell == null) return state;

  const garden = island.garden.slice();
  garden[idx] = {
    ...cell,
    orientation: (((cell.orientation + 1) % 4) as Orientation),
  };
  return writeIsland(state, islandIndex, { garden, plantings: island.plantings });
}

/**
 * 庭全体の属性合計を返す。空セル・無属性デコは寄与しない。
 * M3 の客誘引判定（光≥3 等）の土台になる中核関数。
 *
 * M4: 植物デコ（盆栽）は、デコ定義の基礎属性に加えて成長段階・樹形ぶんの属性を
 * 上乗せする（plantAttributes）。あわせて古木（最終段階）の本数を `ancientTrees`
 * として返し、レア客「樹守ねむ」の条件判定に使えるようにする。
 *
 * M5: 「本日のスペシャル」のドリンクが選ばれていれば、その誘引属性を加算する
 * （メニューが庭属性に統合され、レア客の複合条件の「あと一押し」になる）。
 *
 * v0.6.0:
 *  - **ゆらぎ**: ゆらぐデコ（光るキノコ等）はしおれていると属性が活力(0..1)ぶんに減衰する。
 *    減衰には絶対時刻 `nowMs` が要る。nowMs 省略時は活力 1.0（常に元気）で従来挙動。
 *  - **隣接シナジー**: 上下左右の組み合わせで発動したボーナス属性を加算する（synergy.ts）。
 *    しおれ・成長段階を条件にするルールがあるため、ゆらぎ・成長の評価結果を渡す。
 *
 * 戻り値は全属性キーを含む（0 を明示）ので、判定側で undefined を気にしなくてよい。
 *
 * v0.7.1: 多島化。対象島 `islandIndex`（既定 0 = ホーム島）の庭・植物だけを評価する。
 * シナジー判定・季節結晶固定・ゆらぎは島内で閉じる（島をまたがない）。`includeSpecial`
 * が true（既定）のときだけ本日のスペシャル属性を足す（全島合算では二重加算を避けるため
 * totalGardenAttributes が false で各島を評価し、special を最後に 1 回だけ足す）。
 *
 * @param state        ゲーム状態。
 * @param nowMs        絶対時刻（エポックミリ秒、ゆらぎ評価に使う）。省略時は活力 1.0。
 * @param islandIndex  対象島の index（既定 0 = ホーム島）。
 * @param includeSpecial 本日のスペシャル属性を含めるか（既定 true）。
 */
export function gardenAttributes(
  state: GameState,
  nowMs?: number,
  islandIndex = 0,
  includeSpecial = true,
): GardenAttributeTotals {
  const total: Record<AttributeKey, number> = {
    light: 0,
    scent: 0,
    water: 0,
    calm: 0,
    lively: 0,
    crystal: 0,
    green: 0,
  };
  let ancientTrees = 0;

  const island = state.islands[islandIndex];
  if (!island) return { ...total, ancientTrees };
  const grid = island.garden;
  const islandPlantings = island.plantings;

  for (let idx = 0; idx < grid.length; idx++) {
    const cell = grid[idx];
    if (cell == null) continue;
    const deco = getDeco(cell.decoId);
    if (!deco) continue;

    // タイルレベル（v0.7.0）: デコの基礎属性に level 倍率を掛けてから使う。
    // 植物は「鉢のグレード」を控えめにする方針で、base にだけ倍率を掛け、成長ぶんは
    // 据え置きにする（成長と二重インフレしないように plantAttributes へは leveled base を渡す）。
    const level = cellLevel(cell);
    const leveledBase = leveledAttributes(deco.attributes, level);

    let attrs: AttributeVector = leveledBase;
    if (isPlantDeco(cell.decoId)) {
      const planting = islandPlantings[String(idx)];
      // セーブ移行直後など planting がまだ無い場合は芽（成長 0・ニュートラル樹形）扱い。
      const growSec = planting ? planting.growSec : 0;
      const form = planting
        ? planting.form
        : { branches: 0.5, bend: 0.5, lush: 0.5, trained: 0 };
      attrs = plantAttributes(leveledBase, growSec, form);
      if (isAncient(growSec)) ancientTrees += 1;
    }

    // ゆらぎ: しおれているデコは属性を活力ぶんに減衰させる（しおれ中は 0 に近づく）。
    // nowMs 未指定や非ゆらぎデコは活力 1.0（減衰なし）。
    const vit =
      nowMs != null && isWiltingDeco(cell.decoId)
        ? vitalityAt(cell.decoId, idx, state.seed, nowMs)
        : 1.0;

    for (const key of ATTRIBUTE_KEYS) {
      const v = attrs[key] ?? 0;
      total[key] += vit < 1 ? v * vit : v;
    }
  }

  // 隣接シナジー（v0.6.0）: 発動中ボーナス属性を加算する。しおれ条件のため nowMs から
  // 各セルのしおれ状態を渡す（nowMs 省略時は常に「しおれていない」）。島内で閉じる。
  const synergies = activeSynergies({
    garden: grid,
    plantings: islandPlantings,
    wilted: (i: number) => {
      if (nowMs == null) return false;
      const c = grid[i];
      if (!c || !isWiltingDeco(c.decoId)) return false;
      return vitalityAt(c.decoId, i, state.seed, nowMs) < 0.5;
    },
  });
  const synAttrs = synergyAttributeTotals(synergies);
  for (const key of ATTRIBUTE_KEYS) {
    total[key] += synAttrs[key] ?? 0;
  }

  // 本日のスペシャル（メニュー）の誘引属性を加算する（全島合算では呼び出し側が 1 回だけ足す）。
  if (includeSpecial) {
    const special = specialAttribute(state.menu);
    if (special) total[special.key] += special.amount;
  }

  return { ...total, ancientTrees };
}

/**
 * アンロック済み全島（0..unlockedIslands-1）の庭属性を合算した来店レート用の属性（v0.7.1）。
 *
 * 各島の「庭デコ＋シナジー」属性（includeSpecial=false で評価）を合算し、ancientTrees も
 * 合算する。本日のスペシャル属性（menu 由来）は島に依らないグローバルなので**最後に 1 回だけ**
 * 足す（島ごとに二重加算しない）。拡張島の庭も来店を後押しする＝「庭を広げると客が増える」。
 *
 * @param state ゲーム状態。
 * @param nowMs 絶対時刻（ゆらぎ評価）。省略時は活力 1.0。
 */
export function totalGardenAttributes(state: GameState, nowMs?: number): GardenAttributeTotals {
  const total: Record<AttributeKey, number> = {
    light: 0, scent: 0, water: 0, calm: 0, lively: 0, crystal: 0, green: 0,
  };
  let ancientTrees = 0;
  const n = Math.min(state.unlockedIslands, state.islands.length);
  for (let i = 0; i < n; i++) {
    // 各島は special を含めず評価（special を島数ぶん二重加算しないため）。
    const a = gardenAttributes(state, nowMs, i, false);
    for (const key of ATTRIBUTE_KEYS) total[key] += a[key];
    ancientTrees += a.ancientTrees ?? 0;
  }
  // スペシャル属性はグローバルに 1 回だけ足す。
  const special = specialAttribute(state.menu);
  if (special) total[special.key] += special.amount;
  return { ...total, ancientTrees };
}

/** 属性の内訳（ステータス画面のバー色分け用）。base = 庭デコ（ゆらぎ込み）、bonus = シナジー＋スペシャル。 */
export interface AttributeBreakdown {
  base: Record<AttributeKey, number>;
  bonus: Record<AttributeKey, number>;
  total: Record<AttributeKey, number>;
}

/**
 * gardenAttributes と同じ計算を「内訳つき」で返す純関数（ステータス画面用）。
 * base = 庭デコ由来（植物の成長・樹形・ゆらぎ減衰を含む）。bonus = 隣接シナジー＋本日の
 * スペシャル。両者の和が total（gardenAttributes の各属性値と一致する）。
 */
export function gardenAttributeBreakdown(
  state: GameState,
  nowMs?: number,
  islandIndex = 0,
  includeSpecial = true,
): AttributeBreakdown {
  const base: Record<AttributeKey, number> = {
    light: 0, scent: 0, water: 0, calm: 0, lively: 0, crystal: 0, green: 0,
  };
  const bonus: Record<AttributeKey, number> = {
    light: 0, scent: 0, water: 0, calm: 0, lively: 0, crystal: 0, green: 0,
  };

  const island = state.islands[islandIndex];
  if (!island) {
    return { base, bonus, total: { ...base } };
  }
  const grid = island.garden;
  const islandPlantings = island.plantings;

  for (let idx = 0; idx < grid.length; idx++) {
    const cell = grid[idx];
    if (cell == null) continue;
    const deco = getDeco(cell.decoId);
    if (!deco) continue;
    const level = cellLevel(cell);
    const leveledBase = leveledAttributes(deco.attributes, level);
    let attrs: AttributeVector = leveledBase;
    if (isPlantDeco(cell.decoId)) {
      const planting = islandPlantings[String(idx)];
      const growSec = planting ? planting.growSec : 0;
      const form = planting ? planting.form : { branches: 0.5, bend: 0.5, lush: 0.5, trained: 0 };
      attrs = plantAttributes(leveledBase, growSec, form);
    }
    const vit =
      nowMs != null && isWiltingDeco(cell.decoId)
        ? vitalityAt(cell.decoId, idx, state.seed, nowMs)
        : 1.0;
    for (const key of ATTRIBUTE_KEYS) {
      const v = attrs[key] ?? 0;
      base[key] += vit < 1 ? v * vit : v;
    }
  }

  const synergies = activeSynergies({
    garden: grid,
    plantings: islandPlantings,
    wilted: (i: number) => {
      if (nowMs == null) return false;
      const c = grid[i];
      if (!c || !isWiltingDeco(c.decoId)) return false;
      return vitalityAt(c.decoId, i, state.seed, nowMs) < 0.5;
    },
  });
  const synAttrs = synergyAttributeTotals(synergies);
  for (const key of ATTRIBUTE_KEYS) bonus[key] += synAttrs[key] ?? 0;

  if (includeSpecial) {
    const special = specialAttribute(state.menu);
    if (special) bonus[special.key] += special.amount;
  }

  const total: Record<AttributeKey, number> = { ...base };
  for (const key of ATTRIBUTE_KEYS) total[key] = base[key] + bonus[key];
  return { base, bonus, total };
}

/**
 * アンロック済み全島の属性内訳を合算したもの（ステータス画面の全島合算表示用、v0.7.1）。
 * base/bonus を島ごとに（includeSpecial=false で）評価して合算し、スペシャルは bonus に
 * 1 回だけ足す（二重加算回避）。total は base+bonus。
 */
export function totalGardenAttributeBreakdown(state: GameState, nowMs?: number): AttributeBreakdown {
  const base: Record<AttributeKey, number> = {
    light: 0, scent: 0, water: 0, calm: 0, lively: 0, crystal: 0, green: 0,
  };
  const bonus: Record<AttributeKey, number> = {
    light: 0, scent: 0, water: 0, calm: 0, lively: 0, crystal: 0, green: 0,
  };
  const n = Math.min(state.unlockedIslands, state.islands.length);
  for (let i = 0; i < n; i++) {
    const b = gardenAttributeBreakdown(state, nowMs, i, false);
    for (const key of ATTRIBUTE_KEYS) {
      base[key] += b.base[key];
      bonus[key] += b.bonus[key];
    }
  }
  const special = specialAttribute(state.menu);
  if (special) bonus[special.key] += special.amount;
  const total: Record<AttributeKey, number> = { ...base };
  for (const key of ATTRIBUTE_KEYS) total[key] = base[key] + bonus[key];
  return { base, bonus, total };
}
